import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { contactInfo, formSubmissions } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    requirementType: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      requirementType: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    formSubmissions.contact.push({
      ...formData,
      timestamp: new Date().toISOString()
    });

    toast({
      title: "Message Sent Successfully!",
      description: "Our team will contact you within 24 hours.",
    });

    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      city: '',
      requirementType: '',
      message: ''
    });
  };

  const handleWhatsApp = () => {
    const phone = contactInfo.whatsapp.replace(/\s/g, '').replace('+', '');
    window.open(`https://wa.me/${phone}?text=Hi, I'd like to know more about Niloman HX Smart RO systems.`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${contactInfo.phone}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Get in Touch</h1>
          <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
            Have questions? We're here to help you choose the perfect smart RO solution for your needs
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-2 shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl">Send Us a Message</CardTitle>
                <CardDescription className="text-base">
                  Fill out the form below and our team will get back to you within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                      className="border-2"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input 
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 9217401431"
                        required
                        className="border-2"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input 
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                        required
                        className="border-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input 
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Enter your city"
                        required
                        className="border-2"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="requirementType">Requirement Type *</Label>
                      <Select value={formData.requirementType} onValueChange={handleSelectChange} required>
                        <SelectTrigger className="border-2">
                          <SelectValue placeholder="Select requirement" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="home">Home RO System</SelectItem>
                          <SelectItem value="commercial">Commercial RO System</SelectItem>
                          <SelectItem value="retrofit">IoT Retrofit Kit</SelectItem>
                          <SelectItem value="service">Service & Maintenance</SelectItem>
                          <SelectItem value="other">Other Query</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us more about your requirements..."
                      rows={5}
                      className="border-2 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white text-lg py-6"
                  >
                    Submit Request
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Quick Contact Cards */}
              <div className="space-y-4">
                <Card className="border-2 hover:border-cyan-400 transition-all duration-300 cursor-pointer" onClick={handleCall}>
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
                        <Phone className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">Call Us</CardTitle>
                        <CardDescription className="text-base font-medium text-cyan-600">
                          {contactInfo.phone}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 hover:border-cyan-400 transition-all duration-300 cursor-pointer" onClick={handleWhatsApp}>
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                        <MessageCircle className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">WhatsApp Us</CardTitle>
                        <CardDescription className="text-base font-medium text-green-600">
                          {contactInfo.whatsapp}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 hover:border-cyan-400 transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <Mail className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">Email Us</CardTitle>
                        <CardDescription className="text-base font-medium text-purple-600">
                          {contactInfo.email}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card className="border-2 hover:border-cyan-400 transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">Visit Us</CardTitle>
                        <CardDescription className="text-base font-medium text-gray-700 leading-relaxed">
                          {contactInfo.address}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </div>

              {/* Office Hours */}
              <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-teal-50">
                <CardHeader>
                  <CardTitle className="text-2xl">Office Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Monday - Friday:</span>
                    <span className="text-gray-900 font-semibold">9:00 AM - 7:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Saturday:</span>
                    <span className="text-gray-900 font-semibold">10:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Sunday:</span>
                    <span className="text-gray-900 font-semibold">Closed</span>
                  </div>
                  <div className="pt-3 border-t border-cyan-300">
                    <p className="text-sm text-gray-600">
                      Emergency support available 24/7 for existing customers
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Prefer to Chat Instantly?
          </h2>
          <p className="text-lg mb-8 text-cyan-50">
            Connect with us on WhatsApp for quick responses to your queries
          </p>
          <Button 
            onClick={handleWhatsApp}
            size="lg" 
            className="bg-green-500 hover:bg-green-600 text-white text-lg px-8"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Chat on WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;
